import React, { useState, useEffect } from 'react';
import brandLogoImage from '../assets/images/simatech_logo.png';
import { useCms } from '../context/CmsContext';

interface WebsiteLoaderProps {
  isLoading?: boolean;
}

export const WebsiteLoader: React.FC<WebsiteLoaderProps> = ({ isLoading = false }) => {
  const [mounted, setMounted] = useState<boolean>(false);
  const [shouldRender, setShouldRender] = useState<boolean>(isLoading);
  const [isFadingOut, setIsFadingOut] = useState<boolean>(false);
  const { siteData } = useCms();
  const brand = siteData?.brand;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;

    if (!isLoading) {
      setIsFadingOut(true);
      const unmountTimer = setTimeout(() => {
        setShouldRender(false);
      }, 300);
      return () => clearTimeout(unmountTimer);
    } else {
      setShouldRender(true);
      setIsFadingOut(false);
    }
  }, [isLoading, mounted]);

  // Do not render if not loading or already completed
  if (!isLoading || !mounted || !shouldRender) return null;

  const companyName = brand?.name || 'SIMATECH AUTOMATION';
  const tagline =
    brand?.tagline || 'Industrial Process Automation Solution Provider & Siemens Channel Partner';

  return (
    <aside
      aria-label="Website Loading"
      aria-live="polite"
      className={`fixed inset-0 z-[999999] flex flex-col items-center justify-center bg-[#021215] text-white transition-all duration-400 ease-[cubic-bezier(0.16,1,0.3,1)] select-none ${
        isFadingOut ? 'opacity-0 pointer-events-none scale-[1.02]' : 'opacity-100'
      }`}
    >
      {/* Dynamic Ambient Background Glow Flares */}
      <div className="pointer-events-none absolute -top-28 -left-28 w-[420px] h-[420px] bg-[#38c9de]/15 rounded-full blur-[120px] animate-pulse" />
      <div className="pointer-events-none absolute -bottom-28 -right-28 w-[420px] h-[420px] bg-[#00a499]/20 rounded-full blur-[120px] animate-pulse [animation-delay:1s]" />

      {/* Center Branding & Animated Monogram */}
      <div className="relative z-10 flex flex-col items-center text-center px-6 max-w-lg w-full">
        
        {/* Monogram in Center with Rotating Precision Ring */}
        <div className="relative w-24 h-24 sm:w-28 sm:h-28 flex items-center justify-center mb-6">
          {/* Outer Ambient Glow */}
          <div className="absolute inset-0 rounded-full bg-[#38c9de]/10 blur-xl animate-pulse" />

          {/* Rotating Precision Circuit Ring */}
          <div
            className="absolute inset-0 rounded-full border-2 border-white/10 border-t-[#38c9de] border-r-[#00a499] animate-spin"
            style={{ animationDuration: '1.4s' }}
          />

          {/* Inner Static Soft Ring */}
          <div className="absolute inset-2 rounded-full border border-white/10 bg-[#06242a]/80 backdrop-blur-md shadow-2xl flex items-center justify-center" />

          {/* Center Brand Monogram */}
          <div className="relative z-10 w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center p-1">
            <img
              src="/simatech-logo-white.png"
              alt="Simatech Monogram"
              className="w-full h-full object-contain drop-shadow-[0_0_16px_rgba(56,201,222,0.6)] animate-pulse"
              onError={(e) => {
                // Fallback to favicon or brand logo
                (e.target as HTMLImageElement).src = '/favicon.png';
              }}
            />
          </div>
        </div>

        {/* Company Name in Bold Text */}
        <div role="heading" aria-level={2} className="text-lg sm:text-xl md:text-2xl font-extrabold tracking-[0.18em] uppercase text-white font-['General_Sans',sans-serif] mb-2 drop-shadow-[0_2px_14px_rgba(56,201,222,0.5)]">
          {companyName}
        </div>

        {/* Tagline in Text */}
        <p className="text-xs sm:text-[13px] font-medium tracking-wide text-[#8de5f2]/90 max-w-md mx-auto leading-relaxed mb-6 font-['General_Sans',sans-serif]">
          {tagline}
        </p>

        {/* Sleek Laser Energy Progress Bar */}
        <div className="w-52 sm:w-64 h-[2.5px] bg-white/10 rounded-full overflow-hidden relative shadow-inner">
          <div
            className="h-full bg-gradient-to-r from-[#1b7481] via-[#38c9de] to-[#6ee7b7] rounded-full animate-loader-progress"
          />
        </div>

        {/* Telemetry Status Indicator */}
        <div className="flex items-center gap-2 mt-4 text-[10.5px] font-mono tracking-widest text-slate-400 uppercase">
          <span className="w-1.5 h-1.5 rounded-full bg-[#38c9de] animate-ping" />
          <span>SYSTEM INITIALIZING</span>
        </div>
      </div>
    </aside>
  );
};
